import { Injectable } from '@angular/core';
import { SQLite, SQLiteObject } from '@awesome-cordova-plugins/sqlite/ngx';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { Logger } from '@pfizer/newton-jetpack-utils';
import { RecordObject, StorageKey } from 'user.interface';

@Injectable()
export class StorageService {
  private readonly databaseName = 'jetpack.app.db';
  private databaseObject: SQLiteObject;

  constructor(
    private readonly ionicStorage: Storage,
    private readonly platform: Platform,
    private readonly sqlLite: SQLite,
    public logger: Logger,
  ) { }

  public async initDatabase(key: string) {
    if (this.platform.is('capacitor')) {
      return this.sqlLite.create({ name: this.databaseName, key, location: 'default' }).then(db => {
        this.databaseObject = db;
      }).catch(async () => {
        // If encryption key does not work create a new db.
        this.recreateDb(key);
      });
    } else {
      await this.ionicStorage.create();
    }
  }

  public changeKey(encryptionKey) {
    this.databaseObject.close();

    return this.initDatabase(encryptionKey);
  }

  public async deleteTable(table: string) {
    if (this.platform.is('capacitor')) {
      return this.databaseObject.executeSql(`DROP TABLE IF EXISTS [${table}]`, null);
    } else {
      this.ionicStorage.clear();
    }
  }

  public async initTable(table: string) {
    if (this.platform.is('capacitor')) {
      return this.databaseObject.executeSql(`CREATE TABLE IF NOT EXISTS [${table}] (key VARCHAR(100) NOT NULL, value BLOB, PRIMARY KEY (key));`, null);
    }
  }

  // Saves store to database
  public async save(storageKey: string, store: RecordObject): Promise<RecordObject> {
    const objectKeys = Object.keys(store);
    if (this.platform.is('capacitor')) {
      const scriptBatch = [];
      // Update record if it exists, insert record if record does not already exist
      objectKeys.forEach((key) => {
        const value = this.escapeData(JSON.stringify(store[key]));
        scriptBatch.push([`INSERT OR IGNORE INTO [${storageKey}] VALUES (?, ?)`, [`${key}`, `'${value}'`]]);
        scriptBatch.push(`UPDATE [${storageKey}] SET value = '${value}' WHERE key = '${key}'`);
      });

      return this.databaseObject.sqlBatch(scriptBatch);
    } else {
      for (const key of objectKeys) {
        await this.ionicStorage.set(key, JSON.stringify(store[key]));
      }
    }
  }

  // Retrieves store from database and returns JSON object
  public async get(storageKey: string): Promise<StorageKey> {
    if (this.platform.is('capacitor')) {
      const queryKeys = `SELECT key, value FROM [${storageKey}]`;
      const result = await this.databaseObject.executeSql(queryKeys, []);
      const returnObject = {};

      for (let i = 0; i < result.rows.length; i++) {
        let value = result.rows.item(i).value;
        if (value && value !== 'undefined') {
          value = JSON.parse(value);
        }
        returnObject[result.rows.item(i).key] = value;
      }

      return returnObject;
    } else {
      const store = {};
      const keys = await this.ionicStorage.keys();
      for (const key of keys) {
        store[key] = JSON.parse(await this.ionicStorage.get(key));
      }

      return store;
    }
  }

  public async recreateDb(key: string) {
    await this.sqlLite.deleteDatabase({ name: this.databaseName, location: 'default' });

    return this.initDatabase(key);
  }

  // Needed to escape data that contains single quotes
  public escapeData(data: string) {
    if (data) {
      return data.replace(/'/g, '\'\'');
    }

    return data;
  }
}
